import AddIcon from "@mui/icons-material/Add";
import SecurityIcon from "@mui/icons-material/Security";
import TuneIcon from "@mui/icons-material/Tune";
import { Popover } from "@mui/material";
import { useRef, useState } from "react";

import { RobotoTooltip } from "@/shared/components";

import styles from "./MoreOptionsButton.module.css";

interface MoreOptionsButtonProps {
  onOpenToolSettings: () => void;
  onOpenContextSettings: () => void;
}

/**
 * "+" button that opens a popover with additional options for the chat
 * input area: tool permissions and context settings.
 */
export function MoreOptionsButton({
  onOpenToolSettings,
  onOpenContextSettings,
}: MoreOptionsButtonProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleItemClick = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      <RobotoTooltip title="More options" placement="top">
        <button
          ref={anchorRef}
          type="button"
          className={styles.button}
          onClick={() => setOpen((prev) => !prev)}
          aria-label="More options"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <AddIcon className={styles.icon} />
        </button>
      </RobotoTooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { className: styles.popover },
        }}
      >
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => handleItemClick(onOpenToolSettings)}
        >
          <SecurityIcon className={styles.menuItemIcon} />
          Tool permissions
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => handleItemClick(onOpenContextSettings)}
        >
          <TuneIcon className={styles.menuItemIcon} />
          Context settings
        </button>
      </Popover>
    </>
  );
}
